import * as Sentry from "@sentry/react";
// Import from the `attribution` subpath so every reporter (onCLS/onINP/onLCP/onTTFB)
// receives attribution-enabled metrics. For INP this attaches an `attribution` payload
// (interactionTarget/interactionType/loadState/…) that pinpoints the slow interaction.
import { onCLS, onINP, onLCP, onTTFB, type INPMetricWithAttribution, type Metric } from "web-vitals/attribution";
import { env } from "@infra/config/env";

/**
 * Extract the INP attribution fields we forward to Sentry. Only INP metrics carry
 * attribution; every other web-vital returns `undefined`. Because the reporters come from
 * `web-vitals/attribution`, INP metrics arrive as `INPMetricWithAttribution` at runtime even
 * though the shared reporter is typed against the base `Metric` (which has no `attribution`).
 */
function inpAttributionData(metric: Metric) {
	if (metric.name !== "INP") return undefined;
	const { attribution } = metric as INPMetricWithAttribution;
	if (!attribution) return undefined;
	return {
		interactionTarget: attribution.interactionTarget,
		interactionType: attribution.interactionType,
		loadState: attribution.loadState,
		// Summarize rather than forward the raw PerformanceEventTiming[]: only the count
		// is useful in the breadcrumb/extra, and the full array is a verbose payload at
		// 100% tracing. interactionTarget/interactionType/loadState are the queryable fields.
		processedEventEntryCount: attribution.processedEventEntries.length,
	};
}

/**
 * Default console reporter with color-coded output based on rating.
 */
function consoleReporter(metric: Metric) {
	const rating = metric.rating;
	const color = rating === "good" ? "green" : rating === "needs-improvement" ? "orange" : "red";
	console.log(
		`%c[Web Vital] ${metric.name}: ${metric.value.toFixed(2)} (${rating})`,
		`color: ${color}; font-weight: bold`,
	);
}

/**
 * Sentry metric unit for each Web Vital. CLS is a unitless layout-shift score; the rest
 * are durations in milliseconds.
 */
function webVitalUnit(metric: Metric): string | undefined {
	return metric.name === "CLS" ? undefined : "millisecond";
}

/**
 * Send Web Vitals to Sentry.
 * - Adds a breadcrumb for every metric, so an error later in the session shows the vitals
 *   that preceded it.
 * - Records every metric as a `web_vital.<name>` distribution in Sentry Metrics, with its
 *   rating as an attribute. Poor vitals are performance data, not defects: alert on the
 *   distribution (e.g. p75 of `web_vital.inp`, or the count where `rating` is `poor`)
 *   rather than opening an issue per slow page load.
 */
function sentryReporter(metric: Metric) {
	// INP metrics carry attribution (which element/interaction was slow); undefined otherwise.
	const attributionData = inpAttributionData(metric);

	// Add breadcrumb for all Web Vitals
	Sentry.addBreadcrumb({
		category: "web-vital",
		message: `${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`,
		level: metric.rating === "good" ? "info" : metric.rating === "needs-improvement" ? "warning" : "error",
		data: {
			name: metric.name,
			value: metric.value,
			rating: metric.rating,
			navigationType: metric.navigationType,
			id: metric.id,
			...attributionData,
		},
	});

	Sentry.metrics.distribution(`web_vital.${metric.name.toLowerCase()}`, metric.value, {
		unit: webVitalUnit(metric),
		attributes: {
			rating: metric.rating,
			navigation_type: metric.navigationType,
			// INP only: which element and interaction kind were slow. The raw entry count is
			// left to the breadcrumb.
			...(attributionData && {
				interaction_target: attributionData.interactionTarget,
				interaction_type: attributionData.interactionType,
				load_state: attributionData.loadState,
			}),
		},
	});
}

export type WebVitalsReporter = "console" | "sentry" | "both" | ((metric: Metric) => void);

/**
 * Initialize Web Vitals performance monitoring.
 *
 * @param reporter - How to report metrics:
 *   - "console": Log to console with color-coded ratings (default in dev)
 *   - "sentry": Send to Sentry as breadcrumbs plus `web_vital.*` distribution metrics
 *   - "both": Log to console AND send to Sentry
 *   - Custom function: Your own reporter
 */
export function initPerformanceMonitoring(reporter: WebVitalsReporter = "console") {
	// In production without VITE_PERF_MONITOR, only run if using Sentry
	const isProduction = !env.isDev;
	const perfMonitorEnabled = env.perfMonitor;

	if (isProduction && !perfMonitorEnabled && reporter === "console") {
		return;
	}

	let report: (metric: Metric) => void;

	if (typeof reporter === "function") {
		report = reporter;
	} else if (reporter === "sentry") {
		report = sentryReporter;
	} else if (reporter === "both") {
		report = (metric) => {
			consoleReporter(metric);
			sentryReporter(metric);
		};
	} else {
		report = consoleReporter;
	}

	onCLS(report);
	onINP(report);
	onLCP(report);
	onTTFB(report);
}
